import { formatLocalTime, DEFAULT_TIMEZONE } from './widgetData';

/**
 * X3.3–X3.5 — the shared terminal states and the stale indicator for the
 * connector-backed widgets.
 *
 * Three rules drive this file (docs/DATA_WIDGET_SPEC.md §4):
 *  - a widget never renders a blank region; every non-ready state is labelled;
 *  - the stale mark is never colour-only (CONNECTORS.md §4) — it carries text;
 *  - the licence attribution is rendered wherever it is required.
 */

const STATE_LABEL: Record<string, string> = {
  'not-configured': 'Not configured',
  pending: 'Waiting for data',
  error: 'Unavailable'
};

/** A labelled terminal state (not-configured / pending / error). */
export function WidgetStateNotice({ status, reason }: { status: string; reason?: string }) {
  return (
    <div className={`player-widget-state player-widget-state-${status}`} data-widget-status={status}>
      <span className="player-widget-state-label">{STATE_LABEL[status] ?? status}</span>
      {reason ? <span className="player-widget-state-reason">{reason}</span> : null}
    </div>
  );
}

/**
 * The stale mark. Always text-bearing (never colour-only) and always shows the
 * last-good time, so a viewer can judge how old the data is.
 */
export function StaleNotice({ lastGood, timezone = DEFAULT_TIMEZONE }: { lastGood: string; timezone?: string }) {
  return (
    <p className="player-widget-stale" data-widget-status="stale">
      <span className="player-widget-stale-tag">STALE</span>
      <span className="player-widget-stale-time">last good {formatLocalTime(lastGood, timezone)}</span>
    </p>
  );
}

/** The licence attribution line, rendered only when the licence requires it. */
export function AttributionLine({ text, url }: { text: string; url?: string }) {
  return (
    <p className="player-widget-attribution">
      {url ? (
        <a href={url} target="_blank" rel="noreferrer">
          {text}
        </a>
      ) : (
        text
      )}
    </p>
  );
}
